import React, { FC, useMemo, useState } from 'react';
import { Modal } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import './viewDetailsModal.scss';
import { DailyVisit } from 'pages/SecurityDashboard/types';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { uniqueId } from 'underscore';

interface ViewDetailsModalProps {
    open: boolean;
    handleCancel: () => void;
    data: DailyVisit | null;
}

const ViewDetailsModal: FC<ViewDetailsModalProps> = ({ open, handleCancel, data }) => {
    const navigate = useNavigate();
    const [showAllVisitors, setShowAllVisitors] = useState<boolean>(false);

    const handleVisitorsButtonClick = () => {
        navigate(`/visitors-on-site/${data?.encryptedId}`);
    };

    const formattedReference = useMemo(() => `00${data?.reference}`.slice(-3), [data?.reference]);

    const renderVisitors = (visitors: string[]) => {
        if (!visitors || visitors.length === 0) {
            return <span className="caption">No visitors</span>;
        }

        const maxVisibleVisitors = 3;
        const hasManyVisitors = visitors.length > maxVisibleVisitors;

        if (!hasManyVisitors) {
            return visitors.map((visitor) => (
                <span key={`${visitor}-${uniqueId()}`} className="caption-visitor-item">
                    {visitor}
                </span>
            ));
        }

        const visibleVisitors = showAllVisitors ? visitors : visitors.slice(0, maxVisibleVisitors);
        const remainingCount = visitors.length - maxVisibleVisitors;

        return (
            <>
                {visibleVisitors.map((visitor) => (
                    <span key={`${visitor}-${uniqueId()}`} className="caption-visitor-item">
                        {visitor}
                    </span>
                ))}
                {!showAllVisitors && remainingCount > 0 && (
                    <div
                        className="visitor-expand-button"
                        onClick={() => setShowAllVisitors(true)}>
                        +{remainingCount} more <DownOutlined />
                    </div>
                )}
                {showAllVisitors && (
                    <div
                        className="visitor-collapse-button"
                        onClick={() => setShowAllVisitors(false)}>
                        Show less <UpOutlined />
                    </div>
                )}
            </>
        );
    };

    return (
        <Modal
            open={open}
            onCancel={handleCancel}
            width={900}
            footer={null}>
            <div className="view-details-modal-container">
                <h2 className="heading">Visit Details</h2>
                <div className="content">
                    <div className="top-section">
                        <div className="reference-wrapper">
                            <h5 className="heading">Reference</h5>
                            <span className="caption">{formattedReference}</span>
                        </div>
                        <div className="site-wrapper">
                            <h5 className="heading">Site</h5>
                            <span className="caption">{data?.siteName}</span>
                        </div>
                        <div className="date-shift-wrapper-modal">
                            <h5 className="heading">Date & Shift</h5>
                            <div className="date-shift-content-modal">
                                <span className="caption-date">{moment(data?.startDate).format('D MMMM YYYY')}</span>
                                <span className="caption-shift">{data?.shift}</span>
                                <span className="caption-time-slot">{data?.timeSlot}</span>
                            </div>
                        </div>
                    </div>
                    <div className="top-section">
                        <div className="visitors-wrapper-modal">
                            <h5 className="heading">Visitors</h5>
                            <div className="visitors-content">
                                {renderVisitors(data?.visitors || [])}
                            </div>
                        </div>
                        <div className="site-wrapper">
                            <h5 className="heading">Area Access</h5>
                            <span className="caption">{data?.areaAccess}</span>
                        </div>
                        <div className="info-wrapper">
                            <h5 className="heading">Work Details</h5>
                            <span className="caption">{data?.workDetails}</span>
                        </div>
                    </div>
                </div>
                {data?.showButton && (
                    <div className="button-wrapper">
                        <button className="visitors-on-site-button" onClick={handleVisitorsButtonClick} type="button">Visitors On Site</button>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ViewDetailsModal;
